import { useState, type FormEvent } from 'react';
import { ApiError, type ApiErrorCode } from '../api/client';
import { readLastTenant, saveLastTenant } from '../lib/lastTenant';
import { useT, type TFn } from '../i18n';

interface Props {
  onSubmit: (input: { email: string; tenantSlug: string }) => Promise<void>;
}

function errorMessage(e: unknown, t: TFn): string {
  if (e instanceof ApiError) {
    const map: Partial<Record<ApiErrorCode, string>> = {
      rate_limited: t('auth.error.rateLimited'),
      network: t('auth.error.network'),
      invalid_payload: t('auth.error.invalidPayload'),
    };
    return map[e.code] ?? `${t('common.errorPrefix')}: ${e.message}`;
  }
  return t('common.unknownError');
}

// ANX-102 — запрос ссылки сброса пароля. Бэкенд анти-enumeration: всегда 204,
// поэтому на успех показываем нейтральное «проверьте почту» вне зависимости
// от существования учётки.
export function ForgotPasswordView({ onSubmit }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState(readLastTenant);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const canSubmit = !pending && email.trim() !== '' && tenantSlug.trim() !== '';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setPending(true);
    const slug = tenantSlug.trim().toLowerCase();
    saveLastTenant(slug);
    try {
      await onSubmit({ email: email.trim().toLowerCase(), tenantSlug: slug });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white p-6 rounded shadow-sm border border-slate-200 w-full max-w-sm">
        <h1 className="text-xl font-semibold mb-2">{t('auth.forgot.title')}</h1>
        {sent ? (
          <>
            <p className="text-sm text-slate-700 mb-4">{t('auth.forgot.sent')}</p>
            <a href="/login" className="text-sm text-accent-600 hover:text-accent-700">
              {t('auth.backToLogin')}
            </a>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">{t('auth.forgot.subtitle')}</p>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full">
              <label className="flex flex-col gap-1">
                <span className="text-sm text-slate-700">{t('auth.email')}</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-2"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-sm text-slate-700">{t('auth.tenant')}</span>
                <input
                  type="text"
                  autoComplete="organization"
                  placeholder={t('auth.tenantHint')}
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-2"
                />
              </label>
              {error !== null && (
                <p role="alert" className="text-sm text-red-600">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={!canSubmit}
                className="bg-slate-900 text-white rounded px-3 py-2 disabled:opacity-50"
              >
                {pending ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
              </button>
              <a
                href="/login"
                className="text-sm text-center text-accent-600 hover:text-accent-700"
              >
                {t('auth.backToLogin')}
              </a>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
